import React from 'react'
import { enableMapSet, produce } from 'immer'
import { Provider, useDispatch, useSelector, useStore } from 'react-redux'
import { createStore } from 'redux'
import store from '../../../../store'
import { useMainStoreSync } from '../../../../common/hooks/useMainStoreSync'
import { JudgmentRuleItemSchema, DataClassificationSchema, DataColumnSchema } from './types'
import { resolveResultDropdownOptions, normalizeResultForDropdown } from './classificationField'
import { mapApiPeriodKindToUi } from './periodKind'

enableMapSet()

const appState = store.getState

/** 判定結果・ELSE判定結果を空にリセットする */
const clearJudgmentResults = (draft) => {
  if (draft.judgmentRules && Array.isArray(draft.judgmentRules)) {
    draft.judgmentRules.forEach((r) => {
      r.result = ''
    })
  }
  draft.elseResult = ''
}

/** プルダウン化時に判定結果・ELSE判定結果を選択肢に合わせて正規化する */
const applyResultDropdownNormalization = (draft) => {
  const opts = draft.resultDropdownOptions || []
  if (opts.length === 0) return

  const hasEmptyOption = draft.resultDropdownHasEmptyOption
  if (draft.judgmentRules && Array.isArray(draft.judgmentRules)) {
    draft.judgmentRules.forEach((r) => {
      r.result = normalizeResultForDropdown(r.result, opts, hasEmptyOption)
    })
  }
  draft.elseResult = normalizeResultForDropdown(draft.elseResult, opts, hasEmptyOption)
}

const initialState = {
  mainStore: appState(),
  // 基本情報
  ruleName: '',
  validStartMonth: '',
  validEndMonth: '',
  // 判定結果の設定先
  periodKind: '', // '' | 'monthly' | 'quarter' | 'yearly'。新規作成時は「選択してください」('')
  targetCategory: '',
  targetCategoryCode: '',
  targetField: '',
  targetFieldCode: '',
  // 判定ルール
  judgmentRules: [{ condition: '', result: '' }],
  elseResult: '',
  selectedConditionIndex: 0, // 初期状態では1つ目の判定ルールを選択。0始まり、-1は未選択
  // データタイプ
  dataType: 'yearly', // 'yearly' | 'monthly' | 'yearEndMonthly'（対象期間が年次のときは yearly / yearEndMonthly）
  // マスタデータ（サブデータAPI レスポンスの使い分け）
  dataClassifications: [], // RANK関数モーダル 項目プルダウン
  rawDataClassifications: [], // SearchKey情報を含む元のdataClassificationsデータ
  classificationItems: [], // 判定結果プルダウン用（ユーザー区分）
  chrofyClassifications: [], // 判定結果プルダウン用（CHROFY規定値区分）
  chrofyClassificationItems: [], // 判定結果プルダウン用（CHROFY規定値項目）
  dataColumns: [], // classificationItems から生成。後に使う実装用（現状 UI 未使用）
  genericDataKinds: [], // サイドバー データ分類の元（TargetPeriod で絞り込み）
  mappingTaskClassifications: [], // 判定結果の設定先 データ分類プルダウン
  chrofyColumns: [], // 判定結果の設定先・サイドバー データ項目
  resultDropdownOptions: [], // 判定結果欄のプルダウンリスト（空以外の ItemValue の配列）
  resultDropdownHasEmptyOption: false, // 紐づく区分項目に「(空)」相当の値が存在するか
  // サイドバーのデータ分類選択状態
  currentYearCategory: '',
  previousYearCategory: '',
  currentMonthCategory: '',
  previousMonthCategory: '',
  currentYearEndMonthCategory: '',
  previousYearEndMonthCategory: '',
  // 編集時のルールID
  ruleId: null,
  // 編集時のルールのStatus
  ruleStatus: null,
  // UI状態
  loading: true,
  errors: {
    server: [],
    client: {}
  }
}

export const judgmentRuleSettingReducer = (state = initialState, action) => {
  return produce(state, (draft) => {
    const { payload, type } = action

    switch (type) {
      case 'UPDATE_FROM_MAIN': {
        draft.mainStore = payload
        break
      }
      case 'INIT': {
        draft.dataClassifications = payload.dataClassifications || []
        draft.rawDataClassifications = payload.rawDataClassifications || []
        draft.classificationItems = payload.classificationItems || []
        draft.chrofyClassifications = payload.chrofyClassifications || []
        draft.chrofyClassificationItems = payload.chrofyClassificationItems || []
        draft.dataColumns = payload.dataColumns || []
        draft.genericDataKinds = payload.genericDataKinds || []
        draft.mappingTaskClassifications = payload.mappingTaskClassifications || []
        draft.chrofyColumns = payload.chrofyColumns || []
        draft.resultDropdownOptions = []
        draft.resultDropdownHasEmptyOption = false
        draft.loading = false

        // 編集時は既存ルール情報を設定
        if (payload.rule) {
          draft.ruleId = payload.rule.RuleId
          draft.ruleStatus = payload.rule.Status || null
          draft.ruleName = payload.rule.RuleName || ''
          // ValidStartMonth/ValidEndMonthを優先的に使用し、フォーマット変換（"YYYY/MM" → "YYYY-MM"）
          // ValidStartMonth/ValidEndMonthがない場合はValidStartDate/ValidEndDateから変換（後方互換性）
          if (payload.rule.ValidStartMonth) {
            draft.validStartMonth = payload.rule.ValidStartMonth.replace(/\//g, '-')
          } else if (payload.rule.ValidStartDate) {
            draft.validStartMonth = payload.rule.ValidStartDate.substring(0, 7)
          } else {
            draft.validStartMonth = ''
          }
          if (payload.rule.ValidEndMonth) {
            draft.validEndMonth = payload.rule.ValidEndMonth.replace(/\//g, '-')
          } else if (payload.rule.ValidEndDate) {
            draft.validEndMonth = payload.rule.ValidEndDate.substring(0, 7)
          } else {
            draft.validEndMonth = ''
          }
          // API の PeriodKind は 'year' / 'quarter' の可能性あり。未設定時は条件式から推定。UI は yearly / quarter / monthly
          draft.periodKind = mapApiPeriodKindToUi(payload.rule.PeriodKind, payload.rule.JudgmentRules)
          draft.targetCategory = payload.rule.DataKindName || ''
          draft.targetCategoryCode = payload.rule.DataKindCode || ''
          draft.targetField = payload.rule.ChrofyColumnName || ''
          draft.targetFieldCode = payload.rule.ChrofyColumnCode || ''
          // コードが未設定で名前のみの既存データの場合、マスタからコードを解決（判定結果の設定先の初期表示用）
          const mappingTaskClassifications = payload.mappingTaskClassifications || []
          const chrofyColumns = payload.chrofyColumns || []
          if (!draft.targetCategoryCode && draft.targetCategory) {
            const cat = mappingTaskClassifications.find(
              (c) => (c.DataClassificationName || '') === draft.targetCategory
            )
            if (cat?.DataClassificationCode) {
              draft.targetCategoryCode = cat.DataClassificationCode
            }
          }
          if (!draft.targetFieldCode && draft.targetField && draft.targetCategoryCode) {
            const col = chrofyColumns.find(
              (c) => c.DataClassificationCode === draft.targetCategoryCode && (c.ColumnName || '') === draft.targetField
            )
            if (col?.ColumnCode) {
              draft.targetFieldCode = col.ColumnCode
            }
          }
          // 条件式内の空文字 "" は画面上 (空) で表示するため、ロード時に変換（内部・API送信時は ""）
          const rawRules = payload.rule.JudgmentRules || [{ condition: '', result: '' }]
          const isNewFormat =
            rawRules.length > 0 &&
            typeof rawRules[0] === 'object' &&
            Object.prototype.hasOwnProperty.call(rawRules[0], 'Condition')
          if (isNewFormat && rawRules.length >= 1) {
            // 新形式: 最後1件はelse、それ以外がIF行
            const ifRules = rawRules.slice(0, -1)
            draft.judgmentRules = ifRules.map((r) => ({
              condition: (r.Condition || '').replace(/""/g, '(空)'),
              result: r.Value ?? ''
            }))
            const lastItem = rawRules[rawRules.length - 1]
            draft.elseResult = (lastItem && lastItem.Value != null ? lastItem.Value : '') || ''
          } else {
            // 旧形式: condition/result とトップレベル ElseResult
            draft.judgmentRules = rawRules.map((r) => ({
              condition: (r.condition || '').replace(/""/g, '(空)'),
              result: r.result ?? ''
            }))
            draft.elseResult = payload.rule.ElseResult || ''
          }

          // 編集時：既存のデータ項目に基づいてプルダウンオプションを計算
          if (draft.targetField) {
            const existingResults = [
              ...(draft.judgmentRules || []).map((r) => r.result).filter((v) => v),
              draft.elseResult || ''
            ].filter((v) => v)
            const { dropdownOptions, hasEmptyOption } = resolveResultDropdownOptions(
              draft.targetField,
              draft.rawDataClassifications || [],
              draft.classificationItems || [],
              {
                additionalOptions: existingResults,
                chrofyClassifications: draft.chrofyClassifications || [],
                chrofyClassificationItems: draft.chrofyClassificationItems || []
              }
            )
            draft.resultDropdownOptions = dropdownOptions
            draft.resultDropdownHasEmptyOption = hasEmptyOption
            applyResultDropdownNormalization(draft)
          }
        } else {
          // 新規作成時は初期状態（対象期間は未選択）
          draft.ruleId = null
          draft.ruleStatus = null
          draft.ruleName = ''
          draft.validStartMonth = ''
          draft.validEndMonth = ''
          draft.periodKind = ''
          draft.targetCategory = ''
          draft.targetCategoryCode = ''
          draft.targetField = ''
          draft.targetFieldCode = ''
          draft.judgmentRules = [{ condition: '', result: '' }]
          draft.elseResult = ''
        }

        // データ読み込み後は1つ目の判定ルールを選択状態にする
        draft.selectedConditionIndex = draft.judgmentRules && draft.judgmentRules.length > 0 ? 0 : -1
        draft.errors = {
          server: [],
          client: {}
        }
        break
      }
      case 'SET_RULE_NAME': {
        draft.ruleName = payload
        break
      }
      case 'SET_VALID_START_MONTH': {
        draft.validStartMonth = payload
        break
      }
      case 'SET_VALID_END_MONTH': {
        draft.validEndMonth = payload
        break
      }
      case 'SET_PERIOD_KIND': {
        draft.periodKind =
          payload === 'yearly' || payload === 'year'
            ? 'yearly'
            : payload === 'quarter'
              ? 'quarter'
              : payload === 'monthly'
                ? 'monthly'
                : (payload ?? '')
        break
      }
      case 'SET_TARGET_PERIOD': {
        // Content の対象期間プルダウンから dispatch。'' | 'monthly' | 'quarter' | 'yearly'
        const newPeriodKind =
          payload === 'yearly' || payload === 'year'
            ? 'yearly'
            : payload === 'quarter'
              ? 'quarter'
              : payload === 'monthly'
                ? 'monthly'
                : (payload ?? '')
        if (draft.periodKind !== newPeriodKind) {
          draft.periodKind = newPeriodKind
          // 対象期間が変わったら判定結果の設定先をクリア（表示されるデータ分類が変わるため）
          draft.targetCategory = ''
          draft.targetCategoryCode = ''
          draft.targetField = ''
          draft.targetFieldCode = ''
          draft.resultDropdownOptions = []
          draft.resultDropdownHasEmptyOption = false
        } else {
          draft.periodKind = newPeriodKind
        }
        break
      }
      case 'SET_TARGET_CATEGORY': {
        const isObj = payload != null && typeof payload === 'object'
        const hadDropdown = (draft.resultDropdownOptions || []).length > 0
        draft.targetCategory = isObj ? (payload.category ?? '') : String(payload ?? '')
        draft.targetCategoryCode = isObj ? (payload.categoryCode ?? '') : ''
        // データ項目をリセット
        draft.targetField = ''
        draft.targetFieldCode = ''
        // プルダウンオプションもリセット
        draft.resultDropdownOptions = []
        draft.resultDropdownHasEmptyOption = false
        // プルダウン化が解除された場合のみ判定結果をクリア（テキスト入力のままの場合は維持）
        if (hadDropdown) {
          clearJudgmentResults(draft)
        }
        break
      }
      case 'SET_TARGET_FIELD': {
        const isObj = payload != null && typeof payload === 'object'
        draft.targetField = isObj ? (payload.field ?? '') : String(payload ?? '')
        draft.targetFieldCode = isObj ? (payload.fieldCode ?? '') : ''

        const hadDropdown = (draft.resultDropdownOptions || []).length > 0

        // 判定結果欄のプルダウンリストを計算
        const fieldName = isObj ? (payload.field ?? '') : String(payload ?? '')
        const { dropdownOptions, hasEmptyOption } = resolveResultDropdownOptions(
          fieldName,
          draft.rawDataClassifications || [],
          draft.classificationItems || [],
          {
            chrofyClassifications: draft.chrofyClassifications || [],
            chrofyClassificationItems: draft.chrofyClassificationItems || []
          }
        )
        draft.resultDropdownOptions = dropdownOptions
        draft.resultDropdownHasEmptyOption = hasEmptyOption

        if (dropdownOptions.length > 0) {
          applyResultDropdownNormalization(draft)
        } else if (hadDropdown) {
          clearJudgmentResults(draft)
        }

        break
      }
      case 'SET_DATA_TYPE': {
        draft.dataType = payload
        // データ分類選択状態をリセット
        draft.currentYearCategory = ''
        draft.previousYearCategory = ''
        draft.currentMonthCategory = ''
        draft.previousMonthCategory = ''
        draft.currentYearEndMonthCategory = ''
        draft.previousYearEndMonthCategory = ''
        break
      }
      case 'SET_CURRENT_YEAR_CATEGORY': {
        draft.currentYearCategory = payload
        break
      }
      case 'SET_PREVIOUS_YEAR_CATEGORY': {
        draft.previousYearCategory = payload
        break
      }
      case 'SET_CURRENT_MONTH_CATEGORY': {
        draft.currentMonthCategory = payload
        break
      }
      case 'SET_PREVIOUS_MONTH_CATEGORY': {
        draft.previousMonthCategory = payload
        break
      }
      case 'SET_CURRENT_YEAR_END_MONTH_CATEGORY': {
        draft.currentYearEndMonthCategory = payload
        break
      }
      case 'SET_PREVIOUS_YEAR_END_MONTH_CATEGORY': {
        draft.previousYearEndMonthCategory = payload
        break
      }
      case 'SELECT_CONDITION': {
        draft.selectedConditionIndex = payload
        break
      }
      case 'ADD_RULE': {
        const defaultResult =
          draft.resultDropdownOptions?.length > 0 && !draft.resultDropdownHasEmptyOption
            ? draft.resultDropdownOptions[0]
            : ''
        draft.judgmentRules.push({ condition: '', result: defaultResult })
        break
      }
      case 'DELETE_RULE': {
        if (draft.judgmentRules.length > 1) {
          const index = payload
          draft.judgmentRules.splice(index, 1)
          // 削除されたルールが選択されていた場合は選択を解除
          if (draft.selectedConditionIndex === index) {
            draft.selectedConditionIndex = -1
          } else if (draft.selectedConditionIndex > index) {
            draft.selectedConditionIndex = draft.selectedConditionIndex - 1
          }
        }
        break
      }
      case 'UPDATE_RULE_CONDITION': {
        const { index, condition } = payload
        if (index >= 0 && index < draft.judgmentRules.length) {
          draft.judgmentRules[index].condition = condition
        }
        break
      }
      case 'UPDATE_RULE_RESULT': {
        const { index, result } = payload
        if (index >= 0 && index < draft.judgmentRules.length) {
          draft.judgmentRules[index].result = result
        }
        break
      }
      case 'UPDATE_ELSE_RESULT': {
        draft.elseResult = payload
        break
      }
      case 'RESET_JUDGMENT_RULES': {
        draft.judgmentRules =
          payload.judgmentRules && payload.judgmentRules.length > 0
            ? JSON.parse(JSON.stringify(payload.judgmentRules))
            : [{ condition: '', result: '' }]
        draft.elseResult = payload.elseResult ?? ''
        draft.selectedConditionIndex = draft.judgmentRules && draft.judgmentRules.length > 0 ? 0 : -1
        applyResultDropdownNormalization(draft)
        break
      }
      case 'INSERT_TO_CONDITION': {
        const { text } = payload
        if (draft.selectedConditionIndex >= 0 && draft.selectedConditionIndex < draft.judgmentRules.length) {
          draft.judgmentRules[draft.selectedConditionIndex].condition += text
        }
        break
      }
      case 'CLEAR_CONDITION': {
        if (draft.selectedConditionIndex >= 0 && draft.selectedConditionIndex < draft.judgmentRules.length) {
          draft.judgmentRules[draft.selectedConditionIndex].condition = ''
        }
        break
      }
      case 'DELETE_LAST_CHAR': {
        if (draft.selectedConditionIndex >= 0 && draft.selectedConditionIndex < draft.judgmentRules.length) {
          const condition = draft.judgmentRules[draft.selectedConditionIndex].condition || ''
          if (condition.length === 0) {
            break
          }
          // parseConditionExpression と同じ正規表現で最後の要素（トークン）を特定。(空)は単一トークン
          const pattern =
            /(RANK\([^)]+\)|\[[^\]]+\]|\(空\)|>=|<=|==|!=|>|<|\+|\*|\/|AND|OR|\(|\)|\-?\d+\.?\d*|-|"[^"]*")/g
          let match
          let lastMatch = null
          while ((match = pattern.exec(condition)) !== null) {
            lastMatch = match
          }
          if (lastMatch === null) {
            // トークンが1つもない（区切り文字のみなど）場合は末尾1文字削除でフォールバック
            draft.judgmentRules[draft.selectedConditionIndex].condition = condition.slice(0, -1).replace(/\s+$/, '')
          } else {
            draft.judgmentRules[draft.selectedConditionIndex].condition = condition
              .substring(0, lastMatch.index)
              .replace(/\s+$/, '')
          }
        }
        break
      }
      case 'DELETE_CONDITION_ELEMENT': {
        const { index, elementText } = payload
        if (index >= 0 && index < draft.judgmentRules.length) {
          const condition = draft.judgmentRules[index].condition
          // 要素を削除（簡易実装：文字列置換）
          draft.judgmentRules[index].condition = condition.replace(elementText, '').trim()
        }
        break
      }
      case 'SET_ERRORS': {
        draft.errors = payload
        break
      }
      case 'CLEAR_ERRORS': {
        draft.errors = {
          server: [],
          client: {}
        }
        break
      }
      case 'SET_LOADING': {
        draft.loading = payload
        break
      }
      /** 画面遷移時：入力欄をいったん初期化してローディング表示にする */
      case 'PREPARE_LOAD': {
        draft.loading = true
        draft.ruleName = ''
        draft.validStartMonth = ''
        draft.validEndMonth = ''
        draft.periodKind = ''
        draft.targetCategory = ''
        draft.targetCategoryCode = ''
        draft.targetField = ''
        draft.targetFieldCode = ''
        draft.judgmentRules = [{ condition: '', result: '' }]
        draft.elseResult = ''
        draft.resultDropdownOptions = []
        draft.resultDropdownHasEmptyOption = false
        draft.ruleId = null
        draft.ruleStatus = null
        draft.selectedConditionIndex = -1
        draft.errors = { server: [], client: {} }
        break
      }
      default:
        break
    }
  })
}

/**
 * Dispatchを変更
 *
 * @returns
 */
export const useJudgmentRuleSettingDispatch = () => {
  const _dispatch = useDispatch()
  return (type, payload) => _dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<typeof initialState>} */
export const useJudgmentRuleSettingSelector = useSelector

export const useJudgmentRuleSettingStore = useStore

/**
 * @function JudgmentRuleSettingProviderStore プロバイダー
 * @param {any} props
 * @returns
 * @component
 */
export const JudgmentRuleSettingProviderStore = (props) => {
  const [store] = React.useState(() => createStore(judgmentRuleSettingReducer))
  useMainStoreSync(store)
  return <Provider store={store}>{props.children}</Provider>
}
